/**
 * Trainova AI — Admin Dashboard Page
 *
 * Provides high-level operational overview and role-aware navigation
 * for Platform Administrators and Tenant Administrators with categorized
 * administrative modules and modern executive UI.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n';
import { useAuth } from '../../../stores/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { useTenants } from '../hooks/useTenants';
import { useTenantUsers } from '../hooks/useTenantUsers';
import { useScenarios } from '../../scenarios/hooks/useScenarios';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { CompetencyReadinessCard } from '../../readiness';

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isPlatformAdmin, isTenantAdmin } = usePermissions();

  const { tenants, isLoading: tenantsLoading } = useTenants();
  const { users, isLoading: usersLoading } = useTenantUsers();
  const { scenarios, loading: scenariosLoading } = useScenarios();

  const totalTenants = tenants.length;
  const activeTenants = tenants.filter((t) => t.isActive).length;
  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.isActive).length;
  const publishedScenarios = scenarios.filter((s) => s.status === 'PUBLISHED').length;

  return (
    <div className="admin-page">
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Learner Portal', path: '/' },
          { label: 'Administration Console' },
        ]}
        icon="⚡"
        title="Trainova AI Administration Console"
        badge="Enterprise Control Plane"
        description="Centralized command center to manage tenant organizations, simulation authoring, AI evaluation engines, learner cohorts, and enterprise security."
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(0, 0, 0, 0.25)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '12.5px',
              }}
            >
              <span style={{ opacity: 0.75 }}>Organization:</span>
              <strong style={{ color: '#FFFFFF' }}>{user?.tenantSlug || user?.tenantId || 'TrainerBay'}</strong>
            </div>

            <Link
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '9px 18px',
                borderRadius: '8px',
                background: '#FFFFFF',
                color: '#0B192C',
                fontWeight: 700,
                fontSize: '13px',
                textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.15s ease',
              }}
            >
              <span>🎯</span>
              <span>{t('nav.learner_portal', undefined, 'Learner Portal')}</span>
            </Link>
          </div>
        }
      />

      {/* KPI Overview Grid */}
      <div className="admin-stats-grid" style={{ marginBottom: '36px' }}>
        {isPlatformAdmin ? (
          <>
            <div className="admin-stat-card">
              <div className="admin-stat-card__icon" style={{ background: '#EEF2FF', color: '#3B5BDB' }}>🏢</div>
              <div className="admin-stat-card__content">
                <span className="admin-stat-card__label">{t('admin.stats.total_organizations', undefined, 'Total Client Organizations')}</span>
                <span className="admin-stat-card__value">
                  {tenantsLoading ? '…' : totalTenants}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                  {tenantsLoading ? '…' : `${activeTenants} ${t('admin.stats.active_subscriptions', undefined, 'active enterprise subscriptions')}`}
                </span>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-card__icon" style={{ background: '#ECFDF5', color: '#0F8A5F' }}>🌐</div>
              <div className="admin-stat-card__content">
                <span className="admin-stat-card__label">{t('admin.stats.active_workspaces', undefined, 'Active Workspaces')}</span>
                <span className="admin-stat-card__value" style={{ color: '#0F8A5F' }}>
                  {tenantsLoading ? '…' : activeTenants}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                  {t('admin.stats.live_proctoring', undefined, 'Live & proctoring enabled')}
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="admin-stat-card">
              <div className="admin-stat-card__icon" style={{ background: '#EEF2FF', color: '#3B5BDB' }}>👥</div>
              <div className="admin-stat-card__content">
                <span className="admin-stat-card__label">{t('admin.stats.org_members', undefined, 'Organization Members')}</span>
                <span className="admin-stat-card__value">
                  {usersLoading ? '…' : totalUsers}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                  {usersLoading ? '…' : `${activeUsers} ${t('admin.stats.active_seats', undefined, 'active learner seats')}`}
                </span>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-card__icon" style={{ background: '#ECFDF5', color: '#0F8A5F' }}>🛡️</div>
              <div className="admin-stat-card__content">
                <span className="admin-stat-card__label">{t('admin.stats.active_accounts', undefined, 'Active Accounts')}</span>
                <span className="admin-stat-card__value" style={{ color: '#0F8A5F' }}>
                  {usersLoading ? '…' : activeUsers}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                  {t('admin.stats.compliant_sso', undefined, 'Compliant & SSO verified')}
                </span>
              </div>
            </div>
          </>
        )}

        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{ background: '#F5F3FF', color: '#7C3AED' }}>🎬</div>
          <div className="admin-stat-card__content">
            <span className="admin-stat-card__label">{t('admin.stats.published_cases', undefined, 'Published Simulation Cases')}</span>
            <span className="admin-stat-card__value" style={{ color: '#7C3AED' }}>
              {scenariosLoading ? '…' : publishedScenarios}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
              {scenariosLoading ? '…' : `${scenarios.length} ${t('admin.stats.total_authored_cases', undefined, 'total authored cases')}`}
            </span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{ background: '#FEF3C7', color: '#D97706' }}>⚡</div>
          <div className="admin-stat-card__content">
            <span className="admin-stat-card__label">{t('admin.stats.ai_health', undefined, 'AI Engine Health')}</span>
            <span className="admin-stat-card__value" style={{ color: '#0F8A5F', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
              {t('admin.stats.operational_100', undefined, '100% Operational')}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
              {t('admin.stats.models_online', undefined, 'Dialogue & rubric models online')}
            </span>
          </div>
        </div>
      </div>

      {/* Organization Cohort Competency Readiness & 90-Day Decay Health (PRD §12.1) */}
      <div style={{ marginBottom: '36px' }}>
        <CompetencyReadinessCard
          baselineScore={84.2}
          isAdminView={true}
        />
      </div>

      {/* ========================================================================= */}
      {/* ========================================================================= */}
      {/* SECTION 1: ORGANIZATION & ACCESS GOVERNANCE                               */}
      {/* ========================================================================= */}
      <div style={{ marginBottom: '36px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ width: '4px', height: '22px', background: '#3B5BDB', borderRadius: '4px' }} />
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--navy, #0F172A)', margin: 0 }}>
            {t('admin.nav_section.org_governance', undefined, 'Organization & Access Governance')}
          </h2>
        </div>

        <div className="admin-nav-cards">
          {isPlatformAdmin && (
            <Link to="/admin/tenants" className="admin-nav-card">
              <div className="admin-nav-card__icon" style={{ background: '#EEF2FF', color: '#3B5BDB' }}>🏢</div>
              <div className="admin-nav-card__body">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3>{t('admin.nav.org_management', undefined, 'Organization Management')}</h3>
                  <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Global</span>
                </div>
                <p>{t('admin.nav.org_management_desc', undefined, 'Provision and configure enterprise client tenants, brand guidelines, domains, and global limits.')}</p>
              </div>
              <span className="admin-nav-card__arrow">→</span>
            </Link>
          )}

          {isTenantAdmin && (
            <Link to="/admin/users" className="admin-nav-card">
              <div className="admin-nav-card__icon" style={{ background: '#EEF2FF', color: '#3B5BDB' }}>👥</div>
              <div className="admin-nav-card__body">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3>{t('admin.nav.user_management', undefined, 'User & Role Management')}</h3>
                  <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Core</span>
                </div>
                <p>{t('admin.nav.user_management_desc', undefined, 'Manage learner accounts, assign instructor and manager permissions, and oversee cohort access.')}</p>
              </div>
              <span className="admin-nav-card__arrow">→</span>
            </Link>
          )}

          <Link to="/admin/audit-logs" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#FEF2F2', color: '#DC2626' }}>📜</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.security_audit', undefined, 'Security & Audit Logs')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Immutable</span>
              </div>
              <p>{t('admin.nav.security_audit_desc', undefined, 'Inspect immutable compliance events, administrative mutations, and authentication history.')}</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>

          <Link to="/admin/llm-audit" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#EFF6FF', color: '#2563EB' }}>🤖</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.llm_audit', undefined, 'LLM Execution & Token Audit Trail')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>AI Observability</span>
              </div>
              <p>{t('admin.nav.llm_audit_desc', undefined, 'Audit LLM prompts, input/output token consumptions, model latencies, costs, and raw payloads per attempt and scenario.')}</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>

          {isPlatformAdmin && (
            <Link to="/admin/packages" className="admin-nav-card">
              <div className="admin-nav-card__icon" style={{ background: '#FAF5FF', color: '#7E22CE' }}>📦</div>
              <div className="admin-nav-card__body">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3>{t('admin.nav.subscription_packages', undefined, 'Subscription Packages & Orders')}</h3>
                  <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Super Admin</span>
                </div>
                <p>{t('admin.nav.subscription_packages_desc', undefined, 'Configure SaaS packages, credit quotas, rollover rules, and approve tenant add-on orders.')}</p>
              </div>
              <span className="admin-nav-card__arrow">→</span>
            </Link>
          )}

          <Link to="/admin/billing" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#ECFDF5', color: '#059669' }}>💳</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.billing', undefined, 'Subscription, Credits & Add-Ons')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Billing</span>
              </div>
              <p>{t('admin.nav.billing_desc', undefined, 'Track simulation creation credit balances, rollover status, and order add-on packages or voice passes.')}</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* SECTION 2: TRAINING ANALYTICS & REPORTING                                 */}
      {/* ========================================================================= */}
      <div style={{ marginBottom: '36px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ width: '4px', height: '22px', background: '#0F8A5F', borderRadius: '4px' }} />
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--navy, #0F172A)', margin: 0 }}>
            {t('admin.nav_section.training_analytics', undefined, 'Training Analytics & Reporting')}
          </h2>
        </div>

        <div className="admin-nav-cards">
          <Link to="/admin/analytics" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#ECFDF5', color: '#0F8A5F' }}>📈</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.training_analytics', undefined, 'Enterprise Training Analytics')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Real-time</span>
              </div>
              <p>{t('admin.nav.training_analytics_desc', undefined, 'Real-time simulation completion metrics, competency pass rates, learner drilldowns, and multi-tenant performance.')}</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>

          <Link to="/admin/reports" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#ECFDF5', color: '#0F8A5F' }}>📊</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.reports', undefined, 'Simulation & Competency Reports')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Reports</span>
              </div>
              <p>{t('admin.nav.reports_desc', undefined, 'Review organization pass rates, competency benchmark bars, learner metrics, CSV/JSON data dumps.')}</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* SECTION 3: AI SIMULATION & RUBRICS STUDIO                                 */}
      {/* ========================================================================= */}
      <div style={{ marginBottom: '36px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ width: '4px', height: '22px', background: '#7C3AED', borderRadius: '4px' }} />
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--navy, #0F172A)', margin: 0 }}>
            {t('admin.nav_section.ai_studio', undefined, 'AI Simulation & Rubrics Studio')}
          </h2>
        </div>

        <div className="admin-nav-cards">
          <Link to="/scenarios" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#F5F3FF', color: '#7C3AED' }}>🎬</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.scenario_studio', undefined, 'Scenario & Case Studio')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Studio</span>
              </div>
              <p>{t('admin.nav.scenario_studio_desc', undefined, 'Author, edit, test, and publish simulation cases, customer personas, and competency rubrics.')}</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>

          <Link to="/admin/evaluations" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#F5F3FF', color: '#7C3AED' }}>⚖️</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.evaluation_studio', undefined, 'AI Evaluation Studio')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Scoring Engine</span>
              </div>
              <p>{t('admin.nav.evaluation_studio_desc', undefined, 'Configure evaluation rubrics, scoring prompts, golden set calibration, and publishing lifecycles.')}</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>

          <Link to="/admin/llm-config" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#F5F3FF', color: '#7C3AED' }}>🤖</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.llm_vault', undefined, 'AI Provider & LLM Key Vault')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Encrypted Vault</span>
              </div>
              <p>{t('admin.nav.llm_vault_desc', undefined, 'Configure dialogue LLMs, scoring engines, STT/TTS voice models, and encrypted API keys.')}</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* SECTION 4: GOVERNANCE & CUSTOMIZATION                                     */}
      {/* ========================================================================= */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ width: '4px', height: '22px', background: '#D97706', borderRadius: '4px' }} />
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--navy, #0F172A)', margin: 0 }}>
            {t('admin.nav_section.customization', undefined, 'Customization & Configuration')}
          </h2>
        </div>

        <div className="admin-nav-cards">
          <Link to="/admin/settings/udf" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#FFFBEB', color: '#D97706' }}>🏷️</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.udf', undefined, 'Learner Custom Fields (UDF) & Mapping')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Schema</span>
              </div>
              <p>{t('admin.nav.udf_desc', undefined, 'Configure learner metadata fields (region, cost center, BU), CSV validation rules, and API sync mappings.')}</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>

          <Link to="/admin/settings/notifications" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#FFFBEB', color: '#D97706' }}>🔔</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.notifications', undefined, 'Notification & Email Settings')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>Automations</span>
              </div>
              <p>Configure channel delivery matrix, reminder timing, quiet hours, and branded email templates.</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>

          <Link to="/admin/settings/storage" className="admin-nav-card">
            <div className="admin-nav-card__icon" style={{ background: '#EFF6FF', color: '#0D4FBD' }}>☁️</div>
            <div className="admin-nav-card__body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3>{t('admin.nav.storage', undefined, 'Media & Cloud Storage (AWS S3)')}</h3>
                <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '10.5px' }}>S3 Media Vault</span>
              </div>
              <p>Configure AWS S3 bucket, region, IAM access keys, and run live connectivity tests for video recordings.</p>
            </div>
            <span className="admin-nav-card__arrow">→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
export default AdminDashboardPage;
